<template>
  <div class="form__element"
       :class="{ 'form__element--floating': floatingLabel }">
    <div class="form__select"
         :class="{
          'form__select--negative': inverted,
          'form__select--disabled': disabled,
          'form__select--large': large,
          'form__select--error': hasError}"
         :inert="disabled"
         ref="select">
      <input type="hidden"
             :value="modelValue"
             :aria-describedby="hasError ? `error-${id}-${inputId}` : null"
             :id="'hidden-input-select-' + id + '-' + inputId" />
      <span class="form__label">
        <span class="label" :id="'select-label-' + id + '-' + inputId">{{ label }}</span>
      </span>
      <div :aria-controls="'select-options-' + id + '-' + inputId"
           aria-expanded="false"
           aria-haspopup="listbox"
           @blur.prevent.stop="handleBlur"
           :aria-labelledby="'select-label-' + id + '-' + inputId"
           :id="'select-' + id + '-' + inputId"
           class="form__select-field"
           role="combobox"
           tabindex="0">
        <div class="form__select-field-label">
          <span class="label">{{ selectedOption }}</span>
        </div>
        <div class="form__select-field-action-icon form__select-field-action-icon--closed">
        <span class="icon">
          <svg xmlns="http://www.w3.org/2000/svg" width="34" height="34" fill="currentColor" viewBox="0 0 34 34" aria-labelledby="title-8yrxm77z5"><title id="title-8yrxm77z5">Dropdown öffnen</title>
            <path d="m16.507 18.68 6.508-6.18 1.485 1.41-7.993 7.59L8.5 13.91 10 12.5l6.507 6.18Z"></path>
          </svg>
        </span>
        </div>
      </div>
      <div class="form__select-options"
           role="listbox"
           :id="'select-options-' + id + '-' + inputId"
           :aria-labelledby="id + '-' + inputId"
           tabindex="-1">
        <div role="option"
             v-for="(option, index) in options"
             :key="option.value"
             :aria-selected="option.value === modelValue"
             :data-value="option.value"
             v-on:click="handleOptionClick(option.value)"
             :id="'select-option-' + index + '-' + id + '-' + inputId"
             :class="{'form__select-option--current': option.value === modelValue}"
             class="form__select-option">
          <div class="form__select-option-label">
            <span class="label">{{ option.text }}</span>
          </div>
        </div>
       </div>
    </div>
    <Error v-if="hasError"
           :inverted="inverted"
           :id="'error-' + id + '-' + inputId"
           :message="errorMessage" />
  </div>
</template>

<script setup>
import {computed, ref, watch, useId, onMounted, useTemplateRef} from 'vue';
import Error from './Error.vue';

import Select from "/vendor/skygate/kieser-patternlab/source/javascript/components/select";

const select = useTemplateRef('select');

const props = defineProps({
  id: {
    type: String,
    required: true,
  },
  label: {
    type: String,
    required: true,
  },
  modelValue: {
    type: [String, Number, null],
    default: '',
  },
  options: {
    type: Array,
    required: true,
    validator: (value) => value.every(option => typeof option.value !== 'undefined' && typeof option.text === 'string'),
  },
  rules: {
    type: Array,
    default: () => [],
  },
  forceValidation: {
    type: Boolean,
    default: false,
  },
  inverted: {
    type: Boolean,
    default: false,
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  large: {
    type: Boolean,
    default: true,
  },
  floatingLabel: {
    type: Boolean,
    default: true
  }
});

const emit = defineEmits(['update:modelValue', 'validation-change']);

const internalValue = ref(props.modelValue);
const selectedOption = ref('');

const isTouched = ref(false);
const errorMessage = ref('');
const inputId = useId();

onMounted(() => {
  if(props.modelValue.length) {
    selectedOption.value = props.options.filter((option) => option.value == props.modelValue)[0].text;
    internalValue.value = props.modelValue;
  }

  new Select(select.value);
});

watch(() => props.modelValue, (newValue) => {
  if(newValue.length > 0) {
    selectedOption.value = props.options.filter((option) => option.value == newValue)[0].text;
    internalValue.value = newValue;
  } else {
    selectedOption.value = '';
    internalValue.value = '';
  }
});

watch(() => props.options, () => {
  validate();
});

const validate = () => {
  if(!props.disabled) {
    for (const rule of props.rules) {
      const result = rule(internalValue.value);
      if (typeof result === 'string') {
        errorMessage.value = result;
        emit('validation-change', false);
        return false;
      }
    }
    errorMessage.value = '';
    emit('validation-change', true);
    return true;
  }
};

watch([internalValue, () => props.forceValidation, isTouched], () => {
  if (isTouched.value || props.forceValidation) {
    validate();
  } else {
    errorMessage.value = '';
  }
}, { immediate: true });

const handleOptionClick = (value) => {
  emit('update:modelValue', value);
  select.value.classList.remove('form__select--open');
};

const handleBlur = (event) => {
  if(!event.relatedTarget) {
    isTouched.value = true;
    validate();
  }
};

const hasError = computed(() => (isTouched.value || props.forceValidation) && errorMessage.value !== '');
</script>
